import { State } from './State';
import { html, render } from 'lit-html';
import { API, EVENT_TYPE_LOGIN_STATUS } from './API';
import { Router, Route, STATE_OPERATIONS } from './Router';
import { Account } from './Account';
import { ModalDialog } from './web-components/ModalDialog';

import './web-components/PhysicianPortal';
import './web-components/Busy';
import './web-components/PatientSearch';
import './web-components/PatientProgress';
import './web-components/Welcome';
import './web-components/ForgotPassword';
import './web-components/ForgotUsername';
import './web-components/ResetPassword';
import './web-components/MyAccount';
import './web-components/ChangePassword';
import './web-components/Admin';

const _window = Symbol();
const _document = Symbol();
const _history = Symbol();
const _state = Symbol();
const _template = Symbol();
const _api = Symbol();
const _router = Symbol();
const _account = Symbol();
const _version = Symbol();

const KCK_PATIENTS_STATE_ID = 'AC4D7573-4B6B-48F5-B58D-B48AE1D42047';

export class App {
	constructor(window, document, history, state, api, router, account, version) {
		if(!(window instanceof Window))
			throw new TypeError('window must be an instance of Window.');
		if(!(document instanceof HTMLDocument))
			throw new TypeError('document must be an instance of HTMLDocument.');
		if(!(history instanceof History))
			throw new TypeError('history must be an instance ofstate History.');
		if(!(state instanceof State))
			throw new TypeError('state must be an instance of State.');
		if(!(api instanceof API))
			throw new TypeError('api must be an instance of API.');
		if(!(router instanceof Router))
			throw new TypeError('router must be an instance of Router.');
		if(!(account instanceof Account))
			throw new TypeError('account must be an instance of Account.');
		if(typeof version !== 'string')
			throw new TypeError('version must be a string.');
		
		this[_window] = window;
		this[_document] = document;
		this[_state] = state;
		this[_history] = history;
		this[_api] = api;
		this[_router] = router;
		this[_account] = account;
		this[_version] = version;
		
		this[_template] = html`
			<pp-busy class="app-busy" .document="${this.document}"></pp-busy>
			<physician-portal 
				@show-busy="${e => this.busy.show(e.detail)}}"
				@show-dialog="${e => this.handleShowDialog(e)}"
				@hide-busy="${e => this.busy.hide(e.detail)}"
				@get-state="${e => this.handleGetState(e)}"
				@get-document="${e => this.handleGetDocument(e)}"
				@get-window="${e => this.handleGetWindow(e)}"
				@get-api="${e => this.handleGetAPI(e)}"
				@get-router="${e => this.handleGetRouter(e)}"
				@get-account="${e => this.handleGetAccount(e)}"
				@get-version="${e => this.handleGetVersion(e)}"
			></physician-portal>
		`;

		//add routes
		this.router.routes.add(
			...[
				{
					pathname: '/',
					template: html`
						<pp-welcome></pp-welcome>
					`,
					title: 'Welcome'
				},
				{ 
					pathname: '/search-patients',
					template: html`
						<pp-patient-search
							.stateId="${KCK_PATIENTS_STATE_ID}"
							@patient-selected="${e => this.router.go('/patient-progress', STATE_OPERATIONS.PUSH_STATE, `/patient-progress?orderId=${e.detail.OrderId}`)}"
						></pp-patient-search>
					`,
					title: 'Search Patients'
				},
				{
					pathname: '/patient-progress',
					template: html`
						<pp-patient-progress></pp-patient-progress>
					`,
					title: 'Patient Progress'
				},
				{
					pathname: '/forgot-password',
					template: html`
						<pp-forgot-password></pp-forgot-password>
					`,
					title: 'Forgot Password'
				},
				{
					pathname: '/reset-password',
					template: html`
						<pp-reset-password></pp-reset-password>
					`,
					title: 'Reset Password'
				},
				{
					pathname: '/forgot-username',
					template: html`
						<pp-forgot-username></pp-forgot-username>
					`,
					title: 'Forgot Username'
				},
				{
					pathname: '/my-account',
					template: html`
						<pp-my-account></pp-my-account>
					`,
					title: 'My Account'
				},
				{
					pathname: '/change-password',
					template: html`
						<pp-change-password
							@password-changed="${e => this.router.go('/', STATE_OPERATIONS.REPLACE_STATE, '/')}"
						></pp-change-password>
					`,
					title: 'Change Password'
				},
				{
					pathname: '/admin',
					template: html`
						<pp-admin></pp-admin>
					`,
					title: 'Admin'
				}
			].map(v => Route.fromObject(v))
		);

		//listen to the API to keep the account in sync
		this.api.on(EVENT_TYPE_LOGIN_STATUS, () => {
			this.account.load(
				this.api.accessToken ?
					API.decodeJwt(this.api.accessToken) :
					{}
			);
		});
		
		// attempt to refresh the access token
		(async () => {
			try { await this.api.refresh(); }
			catch(err) { /* do nothing */ }
		})();

		render(this.template, this.document.body);
	}
	handleShowDialog(e) {
		const { title, message, type, callback, width, contents } = e.detail;
		const dialog = new ModalDialog();
		dialog.title = title;
		dialog.type = type;
		dialog.document = document;
		dialog.message = message || '';
		if(width) dialog.width = width;
		if(contents) dialog.contents = contents;
		if(callback) dialog.callback = callback;
		this.document.body.appendChild(dialog);
	}
	handleGetState(e) {
		const { callback } = e.detail;
		if(typeof callback !== 'function')
			throw new TypeError('callback must be a function.');
		
		callback(this.state);
	}
	handleGetDocument(e) {
		const { callback } = e.detail;
		if(typeof callback !== 'function')
			throw new TypeError('callback must be a function.');
		callback(this.document);
	}
	handleGetWindow(e) {
		const { callback } = e.detail;
		if(typeof callback !== 'function')
			throw new TypeError('callback must be a function.');
		callback(this.window);
	}
	handleGetAPI(e) {
		const { callback } = e.detail;
		if(typeof callback !== 'function')
			throw new TypeError('callback must be a function.');
		callback(this.api);
	}
	handleGetRouter(e) {
		const { callback } = e.detail;
		if(typeof callback !== 'function')
			throw new TypeError('callback must be a function.');
		callback(this.router);
	}
	handleGetAccount(e) {
		const { callback } = e.detail;
		if(typeof callback !== 'function')
			throw new TypeError('callback must be a function.');
		callback(this.account);
	}
	handleGetVersion(e) {
		const { callback } = e.detail;
		if(typeof callback !== 'function')
			throw new TypeError('callback must be a function.');
		callback(this.version);
	}
	get window() {
		return this[_window];
	}
	get document() {
		return this[_document];
	}
	get state() {
		return this[_state];
	}
	get history() {
		return this[_history];
	}
	get template() {
		return this[_template];
	}
	get api() {
		return this[_api];
	}
	get router() {
		return this[_router];
	}
	get account() {
		return this[_account];
	}
	get version() {
		return this[_version];
	}
	get busy() {
		return this.document.body.querySelector('pp-busy.app-busy');
	}
}